import { logger } from '@/lib/logger';
import { hasAnalyticsConsent } from '@/lib/consent';

// Analytics utility with GA4 support
export type AnalyticsEvent = {
  name: string;
  properties?: Record<string, unknown>;
};

// GA4 Measurement ID - set this in .env as VITE_GA4_MEASUREMENT_ID
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

// Module-level idempotency guard. AnalyticsListener calls initGA4 both on
// mount (when consent is already stored) and on consent-changed(accepted), and
// React StrictMode / route remounts can repeat either. Without this, each call
// appended another gtag.js tag and re-ran gtag('config') — double-counting.
let ga4Initialized = false;

// Initialize GA4. Callers are responsible for only invoking this once the
// visitor has accepted analytics cookies (see AnalyticsListener).
export const initGA4 = () => {
  if (!GA4_MEASUREMENT_ID) {
    logger.info('[analytics] GA4 not configured - set VITE_GA4_MEASUREMENT_ID');
    return;
  }
  if (ga4Initialized) return;
  ga4Initialized = true;

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  // Consent Mode: storage is denied by default so that even if gtag.js is ever
  // reached without consent it stays cookieless. Because this function only
  // runs after acceptance, the update to granted follows config immediately;
  // it is still re-checked against the stored consent rather than assumed.
  gtag('consent', 'default', { analytics_storage: 'denied' });
  gtag('js', new Date());
  gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false, // We'll send page views manually
  });
  if (hasAnalyticsConsent()) {
    gtag('consent', 'update', { analytics_storage: 'granted' });
  }

  logger.info('[analytics] GA4 initialized with ID:', GA4_MEASUREMENT_ID);
};

export const trackPageView = (path: string) => {
  // Nothing reaches the dataLayer (GTM or GA4) without analytics consent.
  if (!hasAnalyticsConsent()) return;

  // Push to dataLayer for GTM compatibility
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    path,
    ts: Date.now(),
  });

  // Send to GA4 if available
  if (window.gtag && GA4_MEASUREMENT_ID) {
    // gtag otherwise reports document.location in full, query and hash
    // included, on this and every later event, which would undo the caller's
    // sanitising. 'set' makes the sanitised location the page's for events
    // that follow too.
    const page_location = `${window.location.origin}${path}`;
    window.gtag('set', { page_location, page_path: path });
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location,
      page_title: document.title,
    });
  }

  logger.info('[analytics] page_view', { path });
};

export const trackEvent = ({ name, properties = {} }: AnalyticsEvent) => {
  if (!hasAnalyticsConsent()) return;

  // Push to dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: name,
    ...properties,
    ts: Date.now(),
  });

  // Send to GA4 if available
  if (window.gtag && GA4_MEASUREMENT_ID) {
    window.gtag('event', name, properties);
  }

  logger.info('[analytics] event', { name, ...properties });
};

// Track conversions (for important actions)
export const trackConversion = (conversionName: string, value?: number) => {
  trackEvent({
    name: 'conversion',
    properties: {
      conversion_name: conversionName,
      value: value || 0,
    },
  });
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent({
    name: 'form_submission',
    properties: {
      form_name: formName,
      success,
    },
  });
};

// Track CTA clicks
export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent({
    name: 'cta_click',
    properties: {
      cta_name: ctaName,
      location,
    },
  });
};
